<template>
  <Head title="Detail Grafik Data Mahasiswa" />
  <Container>
    <template #header>
      <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
    </template>
    <template #default>
      <Breadcrumbs
        :breadcrumbs="[
          { name: 'Dashboard', href: route('dashboard') },
          { name: 'Grafik Data Mahasiswa', href: route('grafik.index') },
          { name: 'Detail Data', href: '#' },
        ]"
      />

      <!-- Header Section -->
      <div class="mb-4 sm:mb-6">
        <div
          class="flex flex-col sm:flex-row sm:items-center sm:justify-between"
        >
          <div>
            <h3 class="text-lg font-semibold text-gray-900">
              Detail Grafik Data Mahasiswa
            </h3>
            <p class="mt-1 text-sm text-gray-600">
              Informasi lengkap data mahasiswa
              {{ grafik.studyProgram?.name || `Prodi ${grafik.kode_prodi}` }}
            </p>
          </div>
          <div class="flex mt-4 space-x-2 sm:mt-0">
            <Link
              :href="route('grafik.index')"
              class="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-600 transition-colors duration-200 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              <i class="mr-2 fa-solid fa-arrow-left"></i>
              Kembali
            </Link>
            <Link
              :href="route('grafik.edit', grafik.id)"
              class="inline-flex items-center px-3 py-2 text-sm font-medium text-yellow-600 transition-colors duration-200 bg-yellow-100 border border-yellow-300 rounded-md hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
            >
              <i class="mr-2 fa-solid fa-edit"></i>
              Edit
            </Link>
            <button
              @click="deleteGrafik"
              class="inline-flex items-center px-3 py-2 text-sm font-medium text-red-600 transition-colors duration-200 bg-red-100 border border-red-300 rounded-md hover:bg-red-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
            >
              <i class="mr-2 fa-solid fa-trash"></i>
              Hapus
            </button>
          </div>
        </div>
      </div>

      <!-- Success Message -->

      <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 lg:gap-8">
        <!-- Main Content -->
        <div class="order-1 col-span-1 lg:col-span-2 lg:order-1">
          <!-- Detail Information -->
          <div
            class="mb-6 bg-white border border-gray-200 shadow-lg rounded-xl"
          >
            <div
              class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-xl sm:px-6 sm:py-4"
            >
              <h3
                class="flex items-center text-lg font-bold text-white sm:text-xl"
              >
                <i class="mr-2 fa-solid fa-info-circle sm:mr-3"></i>
                Informasi Detail
              </h3>
            </div>
            <div class="p-4 sm:p-6">
              <dl class="grid grid-cols-1 gap-x-4 gap-y-6 sm:grid-cols-2">
                <div>
                  <dt class="text-sm font-medium text-gray-500">
                    Tahun Akademik
                  </dt>
                  <dd class="mt-1 text-sm text-gray-900">
                    {{ grafik.tahun_akademik }}
                  </dd>
                </div>

                <div>
                  <dt class="text-sm font-medium text-gray-500">Semester</dt>
                  <dd class="mt-1">
                    <span
                      :class="[
                        'inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium',
                        grafik.semester === 'ganjil'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-green-100 text-green-800',
                      ]"
                    >
                      {{
                        grafik.semester.charAt(0).toUpperCase() +
                        grafik.semester.slice(1)
                      }}
                    </span>
                  </dd>
                </div>

                <div>
                  <dt class="text-sm font-medium text-gray-500">
                    Kode Program Studi
                  </dt>
                  <dd class="mt-1 text-sm text-gray-900">
                    {{ grafik.kode_prodi }}
                  </dd>
                </div>

                <div>
                  <dt class="text-sm font-medium text-gray-500">
                    Nama Program Studi
                  </dt>
                  <dd class="mt-1 text-sm text-gray-900">
                    {{
                      grafik.studyProgram?.name || `Prodi ${grafik.kode_prodi}`
                    }}
                  </dd>
                </div>

                <div>
                  <dt class="text-sm font-medium text-gray-500">
                    Jumlah Mahasiswa
                  </dt>
                  <dd class="mt-1">
                    <span class="text-2xl font-bold text-blue-600">
                      {{ grafik.jumlah_mahasiswa.toLocaleString() }}
                    </span>
                    <span class="ml-2 text-sm text-gray-500">mahasiswa</span>
                  </dd>
                </div>

                <div>
                  <dt class="text-sm font-medium text-gray-500">ID Record</dt>
                  <dd class="mt-1 text-sm text-gray-900">#{{ grafik.id }}</dd>
                </div>

                <div class="sm:col-span-2" v-if="grafik.keterangan">
                  <dt class="text-sm font-medium text-gray-500">Keterangan</dt>
                  <dd class="mt-1 text-sm text-gray-900">
                    {{ grafik.keterangan }}
                  </dd>
                </div>

                <div>
                  <dt class="text-sm font-medium text-gray-500">Dibuat</dt>
                  <dd class="mt-1 text-sm text-gray-900">
                    {{ formatDate(grafik.created_at) }}
                  </dd>
                </div>

                <div>
                  <dt class="text-sm font-medium text-gray-500">
                    Terakhir Diperbarui
                  </dt>
                  <dd class="mt-1 text-sm text-gray-900">
                    {{ formatDate(grafik.updated_at) }}
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          <!-- Timeline or History -->
          <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
            <div
              class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-green-500 to-blue-600 rounded-t-xl sm:px-6 sm:py-4"
            >
              <h3
                class="flex items-center text-lg font-bold text-white sm:text-xl"
              >
                <i class="mr-2 fa-solid fa-clock sm:mr-3"></i>
                Riwayat Perubahan
              </h3>
            </div>
            <div class="p-4 sm:p-6">
              <div class="flow-root">
                <ul class="-mb-8">
                  <li>
                    <div class="relative pb-8">
                      <span
                        class="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200"
                        aria-hidden="true"
                      ></span>
                      <div class="relative flex space-x-3">
                        <div>
                          <span
                            class="flex items-center justify-center w-8 h-8 bg-green-500 rounded-full ring-8 ring-white"
                          >
                            <i class="w-4 h-4 text-white fa-solid fa-plus"></i>
                          </span>
                        </div>
                        <div
                          class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4"
                        >
                          <div>
                            <p class="text-sm text-gray-500">
                              Data dibuat
                              <span class="font-medium text-gray-900">{{
                                formatDate(grafik.created_at)
                              }}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                  <li v-if="grafik.created_at !== grafik.updated_at">
                    <div class="relative">
                      <div class="relative flex space-x-3">
                        <div>
                          <span
                            class="flex items-center justify-center w-8 h-8 bg-blue-500 rounded-full ring-8 ring-white"
                          >
                            <i class="w-4 h-4 text-white fa-solid fa-edit"></i>
                          </span>
                        </div>
                        <div
                          class="min-w-0 flex-1 pt-1.5 flex justify-between space-x-4"
                        >
                          <div>
                            <p class="text-sm text-gray-500">
                              Data diperbarui
                              <span class="font-medium text-gray-900">{{
                                formatDate(grafik.updated_at)
                              }}</span>
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <!-- Sidebar -->
        <div class="order-2 col-span-1 space-y-6 lg:order-2">
          <!-- Quick Stats -->
          <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
            <div
              class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-purple-500 to-pink-600 rounded-t-xl sm:px-6 sm:py-4"
            >
              <h3
                class="flex items-center text-lg font-bold text-white sm:text-xl"
              >
                <i class="mr-2 fa-solid fa-chart-pie sm:mr-3"></i>
                Statistik Cepat
              </h3>
            </div>
            <div class="p-4 sm:p-6">
              <div class="grid grid-cols-1 gap-4">
                <div class="flex items-center p-3 rounded-lg bg-blue-50">
                  <div class="p-2 text-blue-600 bg-blue-100 rounded-full">
                    <i class="w-5 h-5 fa-solid fa-users"></i>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm font-medium text-gray-600">
                      Jumlah Mahasiswa
                    </p>
                    <p class="text-xl font-bold text-gray-900">
                      {{ grafik.jumlah_mahasiswa.toLocaleString() }}
                    </p>
                  </div>
                </div>

                <div class="flex items-center p-3 rounded-lg bg-green-50">
                  <div class="p-2 text-green-600 bg-green-100 rounded-full">
                    <i class="w-5 h-5 fa-solid fa-graduation-cap"></i>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm font-medium text-gray-600">
                      Kode Program Studi
                    </p>
                    <p class="text-lg font-bold text-gray-900">
                      {{ grafik.kode_prodi }}
                    </p>
                  </div>
                </div>

                <div class="flex items-center p-3 rounded-lg bg-purple-50">
                  <div class="p-2 text-purple-600 bg-purple-100 rounded-full">
                    <i class="w-5 h-5 fa-solid fa-calendar"></i>
                  </div>
                  <div class="ml-3">
                    <p class="text-sm font-medium text-gray-600">Periode</p>
                    <p class="text-lg font-bold text-gray-900">
                      {{ grafik.tahun_akademik }}
                    </p>
                    <p class="text-sm text-purple-500">
                      {{
                        grafik.semester.charAt(0).toUpperCase() +
                        grafik.semester.slice(1)
                      }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Additional Actions -->
          <div class="bg-white border border-gray-200 shadow-lg rounded-xl">
            <div
              class="px-4 py-3 border-b border-gray-200 bg-gradient-to-r from-orange-500 to-red-600 rounded-t-xl sm:px-6 sm:py-4"
            >
              <h3
                class="flex items-center text-lg font-bold text-white sm:text-xl"
              >
                <i class="mr-2 fa-solid fa-cogs sm:mr-3"></i>
                Aksi Tambahan
              </h3>
            </div>
            <div class="p-4 sm:p-6">
              <div class="space-y-3">
                <Link
                  :href="route('grafik.edit', grafik.id)"
                  class="w-full inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-white bg-yellow-600 border border-transparent rounded-lg shadow-sm hover:bg-yellow-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
                >
                  <i class="mr-2 fa-solid fa-edit"></i>
                  Edit Data
                </Link>

                <button
                  @click="duplicateData"
                  class="w-full inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-white bg-blue-600 border border-transparent rounded-lg shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  <i class="mr-2 fa-solid fa-copy"></i>
                  Duplikasi Data
                </button>

                <Link
                  :href="
                    route('grafik.index', {
                      tahun_akademik: grafik.tahun_akademik,
                      semester: grafik.semester,
                    })
                  "
                  class="w-full inline-flex justify-center items-center px-4 py-2.5 text-sm font-semibold text-white bg-indigo-600 border border-transparent rounded-lg shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
                >
                  <i class="mr-2 fa-solid fa-chart-bar"></i>
                  Lihat Data Periode Ini
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Container>
</template>

<script setup>
import { inject } from "vue";
import { Head, Link, router } from "@inertiajs/vue3";
import Container from "@/Layouts/Container.vue";
import Header from "@/Components/Header.vue";
import Breadcrumbs from "@/Components/Breadcrumbs.vue";
import Swal from "sweetalert2";
const route = inject('route');

// Props
const props = defineProps({
  grafik: Object,
});

// Methods
const deleteGrafik = () => {
  Swal.fire({
    title: "Hapus Data Grafik?",
    html: `Anda akan menghapus data:<br><strong>${
      props.grafik.studyProgram?.name || `Prodi ${props.grafik.kode_prodi}`
    }</strong><br>${props.grafik.tahun_akademik}`,
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#ef4444",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, Hapus!",
    cancelButtonText: "Batal",
    reverseButtons: true,
  }).then((result) => {
    if (result.isConfirmed) {
      router.delete(route("grafik.destroy", props.grafik.id), {
        onSuccess: () => {
          Swal.fire({
            title: "Berhasil!",
            text: "Data grafik berhasil dihapus.",
            icon: "success",
            confirmButtonColor: "#10b981",
            timer: 2000,
            timerProgressBar: true,
          }).then(() => {
            // Redirect ke index setelah sukses
            router.visit(route("grafik.index"));
          });
        },
        onError: () => {
          Swal.fire({
            title: "Gagal!",
            text: "Terjadi kesalahan saat menghapus data.",
            icon: "error",
            confirmButtonColor: "#ef4444",
          });
        },
      });
    }
  });
};

const duplicateData = () => {
  Swal.fire({
    title: "Duplikasi Data?",
    html: `Anda akan membuat duplikasi data:<br><strong>${
      props.grafik.studyProgram?.name || `Prodi ${props.grafik.kode_prodi}`
    }</strong><br>${props.grafik.tahun_akademik}`,
    icon: "question",
    showCancelButton: true,
    confirmButtonColor: "#3b82f6",
    cancelButtonColor: "#6b7280",
    confirmButtonText: "Ya, Duplikasi!",
    cancelButtonText: "Batal",
    reverseButtons: true,
  }).then((result) => {
    if (result.isConfirmed) {
      router.post(
        route("grafik.store"),
        {
          tahun_akademik: props.grafik.tahun_akademik,
          semester: props.grafik.semester,
          kode_prodi: props.grafik.kode_prodi,
          jumlah_mahasiswa: props.grafik.jumlah_mahasiswa,
          keterangan: props.grafik.keterangan
            ? props.grafik.keterangan + " (Duplikasi)"
            : "Duplikasi data",
        },
        {
          onSuccess: () => {
            Swal.fire({
              title: "Berhasil!",
              text: "Data berhasil diduplikasi.",
              icon: "success",
              confirmButtonColor: "#10b981",
              timer: 2000,
              timerProgressBar: true,
            });
          },
          onError: () => {
            Swal.fire({
              title: "Gagal!",
              text: "Terjadi kesalahan saat menduplikasi data.",
              icon: "error",
              confirmButtonColor: "#ef4444",
            });
          },
        }
      );
    }
  });
};

const formatDate = (dateString) => {
  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  };
  return new Date(dateString).toLocaleDateString("id-ID", options);
};
</script>
